/**
 * Failure tracking and exponential backoff management for configuration loading
 * Prevents hammering failed configs with retry requests
 */

import { getIsSigningOut } from '../apiFetch';

/**
 * Critical authentication configs that must work for the app to function
 * If these fail, we should redirect to error page
 */
export const CRITICAL_AUTH_CONFIGS = ['auth_pages_config'];

/**
 * Error types for configuration failures
 */
export enum ConfigErrorType {
    NETWORK_ERROR = 'network_error',
    SERVER_ERROR = 'server_error',
    CIRCULAR_CONFIG = 'circular_config',
    UNKNOWN = 'unknown'
}

/**
 * Track failed config attempts and implement exponential backoff
 * Maps config name to { failCount, lastFailTime, nextRetryTime, errorType, errorDetails }
 */
export const failureBackoff = new Map<string, {
    failCount: number;
    lastFailTime: number;
    nextRetryTime: number;
    errorType?: ConfigErrorType;
    errorDetails?: string;
}>();

/**
 * Track if we've already redirected to config error page
 * This prevents multiple redirects
 */
let hasRedirectedToConfigError = false;

/**
 * Get the redirect flag status
 */
export const getHasRedirectedToConfigError = (): boolean => hasRedirectedToConfigError;

/**
 * Set the redirect flag
 */
export const setHasRedirectedToConfigError = (value: boolean): void => {
    hasRedirectedToConfigError = value;
};

/**
 * Calculate the next retry time based on failure count (exponential backoff)
 * Returns delay in milliseconds
 */
export const getBackoffDelay = (failCount: number): number => {
    // Exponential backoff: 5s, 15s, 30s, 1m, 5m, 15m, then 30m max
    const delays = [5000, 15000, 30000, 60000, 300000, 900000, 1800000];
    const index = Math.min(failCount - 1, delays.length - 1);
    return delays[index];
};

/**
 * Check if a config is currently in backoff period
 */
export const isInBackoff = (configName: string): boolean => {
    const backoff = failureBackoff.get(configName);

    if (!backoff) return false;

    const now = Date.now();
    return now < backoff.nextRetryTime;
};

/**
 * Determine error type from error object
 */
const getErrorType = (error: unknown): ConfigErrorType => {
    if (!error) return ConfigErrorType.UNKNOWN;

    // Check for RTK Query error structure
    if (typeof error === 'object' && error !== null) {
        if ('status' in error) {
            const status = (error as { status: string | number }).status;

            // Network errors (FETCH_ERROR, TIMEOUT_ERROR, etc.)
            if (typeof status === 'string') {
                return ConfigErrorType.NETWORK_ERROR;
            }

            // Server errors (5xx) or client errors (4xx)
            if (typeof status === 'number') {
                return status >= 500 ? ConfigErrorType.SERVER_ERROR : ConfigErrorType.NETWORK_ERROR;
            }
        }
    }

    return ConfigErrorType.UNKNOWN;
};

/**
 * Get error details string from error object
 */
const getErrorDetails = (error: unknown): string => {
    if (!error) return 'Unknown error';

    if (typeof error === 'object' && error !== null) {
        if ('status' in error) {
            const status = (error as { status: string | number }).status;

            if (typeof status === 'string') {
                return `Network error: ${status}`;
            }

            if (typeof status === 'number') {
                return `HTTP ${status}`;
            }
        }

        if ('message' in error && typeof (error as { message: unknown }).message === 'string') {
            return (error as { message: string }).message;
        }
    }

    return String(error);
};

/**
 * Session storage key for error details
 */
const CONFIG_ERROR_DETAILS_KEY = 'config_error_details';

/**
 * Record a failed config attempt
 */
export const recordFailure = (configName: string, error?: unknown): void => {
    // Don't record failures during sign-out to prevent race conditions
    if (getIsSigningOut()) {
        return;
    }

    const now = Date.now();
    const existing = failureBackoff.get(configName);
    const failCount = existing ? existing.failCount + 1 : 1;
    const delay = getBackoffDelay(failCount);
    const errorType = getErrorType(error);
    const errorDetails = getErrorDetails(error);

    failureBackoff.set(configName, {
        failCount,
        lastFailTime: now,
        nextRetryTime: now + delay,
        errorType,
        errorDetails
    });

    const isCritical = CRITICAL_AUTH_CONFIGS.includes(configName);

    if (isCritical) {

        // Store error details for display on error page
        try {
            const errorInfo = {
                configName,
                errorType,
                errorDetails,
                failCount,
                timestamp: now
            };
            sessionStorage.setItem(CONFIG_ERROR_DETAILS_KEY, JSON.stringify(errorInfo));
        } catch {
            // Ignore sessionStorage errors
        }
    }

    if (shouldRedirectToConfigError()) {
        redirectToConfigError();
    }
};

/**
 * Check for circular configuration (API URL points to itself)
 */
export const checkCircularConfig = (configValue: unknown): boolean => {
    try {
        if (typeof configValue !== 'object' || !configValue) return false;

        // Check common API URL fields
        const apiUrlFields = ['api_url', 'apiUrl', 'apiURL', 'base_url', 'baseUrl', 'url'];
        const currentOrigin = globalThis.location?.origin;

        if (!currentOrigin) return false;

        for (const field of apiUrlFields) {
            const value = (configValue as Record<string, unknown>)[field];

            if (typeof value === 'string' && value.startsWith(currentOrigin)) {
                return true;
            }
        }

        return false;
    } catch {
        return false;
    }
};

/**
 * Record a circular configuration error
 */
export const recordCircularConfig = (configName: string, _configValue: unknown): void => {
    const now = Date.now();

    failureBackoff.set(configName, {
        failCount: 2, // Set to 2 to trigger immediate redirect
        lastFailTime: now,
        nextRetryTime: now,
        errorType: ConfigErrorType.CIRCULAR_CONFIG,
        errorDetails: 'Server address points to this website'
    });

    try {
        const errorInfo = {
            configName,
            errorType: ConfigErrorType.CIRCULAR_CONFIG,
            errorDetails: 'The server address is set to this website instead of the actual server.',
            failCount: 2,
            timestamp: now
        };
        sessionStorage.setItem(CONFIG_ERROR_DETAILS_KEY, JSON.stringify(errorInfo));
    } catch {
        // Ignore sessionStorage errors
    }

    redirectToConfigError();
};

/**
 * Clear failure tracking for a config (called on successful load)
 */
export const clearFailure = (configName: string): void => {
    failureBackoff.delete(configName);

    if (failureBackoff.size === 0) {
        setHasRedirectedToConfigError(false);
    }
};

/**
 * Clear all failure tracking state
 * Call this when signing out or when you need to reset the failure state completely
 */
export const clearAllFailures = (): void => {
    failureBackoff.clear();
    setHasRedirectedToConfigError(false);
    try {
        sessionStorage.removeItem(CONFIG_ERROR_DETAILS_KEY);
    } catch {
        // Ignore sessionStorage errors
    }
};

/**
 * Check if we should redirect to config error page
 * This only checks critical authentication configs (sign-in/sign-up)
 */
export const shouldRedirectToConfigError = (): boolean => {
    // Don't redirect during sign-out to prevent race conditions
    if (getIsSigningOut()) {
        return false;
    }

    if (getHasRedirectedToConfigError()) {
        return false;
    }

    let criticalFailureCount = 0;

    failureBackoff.forEach((backoff, configName) => {
        if (CRITICAL_AUTH_CONFIGS.includes(configName) && backoff.failCount >= 2) {
            criticalFailureCount++;
        }
    });

    return criticalFailureCount > 0;
};

/**
 * Redirect to config error page
 */
export const redirectToConfigError = (): void => {
    if (getHasRedirectedToConfigError()) {
        return;
    }

    setHasRedirectedToConfigError(true);

    failureBackoff.clear();
    globalThis.location.href = '/config-error';
};
